import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Gift, Share2, ExternalLink, Camera, ListPlus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { UserAvatar } from "@/components/Avatars";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtEur = (n) => `${n.toFixed(2).replace(".", ",")} €`;

const WishItemDialog = ({ open, onOpenChange, item, listId, onSaved }) => {
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.description || "");
  const [url, setUrl] = useState(item?.url || "");
  const [price, setPrice] = useState(item?.price ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) return toast.error("El título es obligatorio");
    setSaving(true);
    try {
      const payload = {
        title, description, url,
        price: price !== "" ? parseFloat(price) : null,
        ...(item && price === "" ? { clear_price: true } : {}),
      };
      if (item) await api.put(`/wishlist/items/${item.id}`, payload);
      else await api.post("/wishlist/items", { ...payload, list_id: listId });
      toast.success(item ? "Deseo actualizado" : "Deseo añadido");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="wish-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? "Editar deseo" : "Nuevo deseo"}</DialogTitle>
          <DialogDescription>Ideas de regalo para quien quiera acertar contigo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>¿Qué deseas?</Label>
            <Input data-testid="wish-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Auriculares inalámbricos" maxLength={200} autoFocus />
          </div>
          <div className="space-y-2">
            <Label>Descripción (opcional)</Label>
            <Textarea data-testid="wish-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} maxLength={1000} placeholder="Color, talla, modelo exacto…" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-2">
              <Label>Enlace de compra (opcional)</Label>
              <Input data-testid="wish-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…" maxLength={500} />
            </div>
            <div className="space-y-2">
              <Label>Precio (€)</Label>
              <Input data-testid="wish-price-input" type="number" min="0" step="0.01" value={price}
                onChange={(e) => setPrice(e.target.value)} placeholder="—" />
            </div>
          </div>
          <Button className="w-full" onClick={save} disabled={saving} data-testid="wish-save-btn">
            {saving ? "Guardando…" : item ? "Guardar cambios" : "Añadir a la lista"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ShareDialog = ({ open, onOpenChange, list, onSaved }) => {
  const { data: home } = useQuery({ queryKey: ["home"], queryFn: async () => (await api.get("/home")).data });
  const workspaces = home?.workspaces || [];
  const [wsIds, setWsIds] = useState(list.shares.workspaces.map((w) => w.id));
  const [emails, setEmails] = useState(list.shares.profiles.map((p) => p.email).join(", "));
  const [saving, setSaving] = useState(false);

  const toggleWs = (id) => setWsIds((prev) => (prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/wishlist/lists/${list.id}/shares`, {
        workspace_ids: wsIds,
        emails: emails.split(",").map((e) => e.trim()).filter(Boolean),
      });
      if (data.not_found.length) toast.warning(`Sin cuenta en Nuxxum: ${data.not_found.join(", ")}`);
      else toast.success("Preferencias de compartir guardadas");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="wish-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Compartir "{list.name}"</DialogTitle>
          <DialogDescription>Quienes la vean tendrán ideas de regalo para ti.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Con mis Espacios</Label>
            <div className="space-y-1.5 max-h-36 overflow-y-auto rounded-lg border p-2">
              {workspaces.map((w) => (
                <label key={w.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={wsIds.includes(w.id)} onCheckedChange={() => toggleWs(w.id)}
                    data-testid={`wish-share-ws-${w.id}`} />
                  {w.icon} {w.name}
                </label>
              ))}
              {workspaces.length === 0 && <p className="text-xs text-muted-foreground">No tienes Espacios.</p>}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Con personas concretas (emails separados por coma)</Label>
            <Input data-testid="wish-share-emails-input" value={emails} onChange={(e) => setEmails(e.target.value)}
              placeholder="ana@ejemplo.com, luis@ejemplo.com" />
          </div>
          <Button className="w-full" onClick={save} disabled={saving} data-testid="wish-share-save-btn">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const WishCard = ({ item, readOnly, onEdit, onDelete, onUpload }) => (
  <Card className="overflow-hidden flex flex-col" data-testid={`wish-card-${item.id}`}>
    {item.image_url && (
      <img src={`${BACKEND}${item.image_url}`} alt={item.title} loading="lazy" className="h-36 w-full object-cover" />
    )}
    <div className="p-4 space-y-2 flex-1 flex flex-col">
      <div className="flex items-start gap-2">
        <p className="text-sm font-semibold flex-1">{item.title}</p>
        {item.price != null && <Badge variant="secondary" className="shrink-0">{fmtEur(item.price)}</Badge>}
      </div>
      {item.description && <p className="text-xs text-muted-foreground line-clamp-3">{item.description}</p>}
      <div className="flex items-center gap-1 mt-auto pt-2">
        {item.url && (
          <a href={item.url} target="_blank" rel="noreferrer"
            className="text-xs text-primary flex items-center gap-1 hover:underline mr-auto" data-testid={`wish-link-${item.id}`}>
            <ExternalLink className="h-3 w-3" /> Ver en tienda
          </a>
        )}
        {!readOnly && (
          <>
            <span className="mr-auto" />
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onUpload} title="Añadir foto"
              data-testid={`wish-photo-btn-${item.id}`}>
              <Camera className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit} data-testid={`wish-edit-btn-${item.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={onDelete}
              data-testid={`wish-delete-btn-${item.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  </Card>
);

const ListSection = ({ list, onRefresh, onAddItem, onEditItem, onShare, onUpload }) => {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(list.name);
  const sharedCount = list.shares.workspaces.length + list.shares.profiles.length;

  const rename = async () => {
    if (!name.trim() || name === list.name) return setRenaming(false);
    try {
      await api.put(`/wishlist/lists/${list.id}`, { name });
      toast.success("Lista renombrada");
      onRefresh();
    } catch (e) {
      toast.error(apiError(e));
    }
    setRenaming(false);
  };

  const removeList = async () => {
    try {
      await api.delete(`/wishlist/lists/${list.id}`);
      toast.success("Lista eliminada");
      onRefresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeItem = async (item) => {
    try {
      await api.delete(`/wishlist/items/${item.id}`);
      onRefresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <section className="space-y-3" data-testid={`wishlist-section-${list.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {renaming ? (
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 w-56"
            onBlur={rename} onKeyDown={(e) => e.key === "Enter" && rename()} autoFocus
            data-testid={`wishlist-rename-input-${list.id}`} />
        ) : (
          <button className="font-heading font-bold text-lg hover:text-primary flex items-center gap-1.5"
            onClick={() => setRenaming(true)} title="Renombrar" data-testid={`wishlist-name-${list.id}`}>
            🎁 {list.name} <Pencil className="h-3 w-3 opacity-40" />
          </button>
        )}
        <Badge variant="secondary" data-testid={`wishlist-total-${list.id}`}>Total: {fmtEur(list.total)}</Badge>
        <Badge variant="outline" className="text-xs">{list.items.length} {list.items.length === 1 ? "deseo" : "deseos"}</Badge>
        <span className="flex-1" />
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => onShare(list)}
          data-testid={`wishlist-share-btn-${list.id}`}>
          <Share2 className="h-3.5 w-3.5 mr-1" /> Compartir{sharedCount > 0 ? ` (${sharedCount})` : ""}
        </Button>
        <Button size="sm" className="h-8 text-xs" onClick={() => onAddItem(list)} data-testid={`wishlist-add-item-btn-${list.id}`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Deseo
        </Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={removeList}
          data-testid={`wishlist-delete-btn-${list.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {list.items.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <p className="text-xs text-muted-foreground">Lista vacía. Añade tu primer deseo.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.items.map((i) => (
            <WishCard key={i.id} item={i}
              onEdit={() => onEditItem(i, list)}
              onDelete={() => removeItem(i)}
              onUpload={() => onUpload(i)} />
          ))}
        </div>
      )}
    </section>
  );
};

export default function WishlistPage() {
  const qc = useQueryClient();
  const [itemDialog, setItemDialog] = useState(null);
  const [shareList, setShareList] = useState(null);
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const fileRef = useRef(null);
  const uploadTarget = useRef(null);

  const { data: mine } = useQuery({ queryKey: ["wishlist"], queryFn: async () => (await api.get("/wishlist")).data });
  const { data: shared = [] } = useQuery({ queryKey: ["wishlists-shared"], queryFn: async () => (await api.get("/wishlists/shared")).data });

  const refresh = () => qc.invalidateQueries({ queryKey: ["wishlist"] });
  const lists = mine?.lists || [];

  const createList = async () => {
    if (!newListName.trim()) return toast.error("Ponle un nombre a la lista");
    try {
      await api.post("/wishlist/lists", { name: newListName });
      toast.success("Lista creada");
      setNewListName("");
      setNewListOpen(false);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const pickPhoto = (item) => {
    uploadTarget.current = item.id;
    fileRef.current?.click();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadTarget.current) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("La imagen no puede superar 5 MB");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/wishlist/items/${uploadTarget.current}/image`, fd);
      toast.success("Foto añadida");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-10" data-testid="wishlist-page">
      <div className="flex flex-wrap items-center gap-3 animate-fade-up">
        <Gift className="h-6 w-6 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex-1">Mis WishList</h1>
        <Button size="sm" onClick={() => setNewListOpen(true)} data-testid="wishlist-create-list-btn">
          <ListPlus className="h-4 w-4 mr-1" /> Nueva lista
        </Button>
      </div>

      {lists.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="wishlist-empty">
          <Gift className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm font-medium">Aún no tienes ninguna lista de deseos</p>
          <p className="text-xs text-muted-foreground mt-1">Crea listas distintas: Lista Regalos, Lista Reforma Habitación…</p>
        </Card>
      ) : (
        <div className="space-y-10">
          {lists.map((l) => (
            <ListSection key={l.id} list={l} onRefresh={refresh}
              onAddItem={(list) => setItemDialog({ list, item: null })}
              onEditItem={(item, list) => setItemDialog({ list, item })}
              onShare={setShareList}
              onUpload={pickPhoto} />
          ))}
        </div>
      )}

      {shared.length > 0 && (
        <section className="space-y-6" data-testid="wishlist-shared-section">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Compartidas conmigo</h2>
          {shared.map((s) => (
            <div key={s.owner.id} className="space-y-4">
              <div className="flex items-center gap-2">
                <UserAvatar name={s.owner.name} color={s.owner.avatar_color} src={s.owner.avatar_url} size="sm" />
                <p className="text-sm font-medium">WishList de {s.owner.name}</p>
              </div>
              {s.lists.map((l) => (
                <div key={l.id} className="space-y-2 pl-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">🎁 {l.name}</p>
                    <Badge variant="secondary" className="text-xs">Total: {fmtEur(l.total)}</Badge>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {l.items.map((i) => <WishCard key={i.id} item={i} readOnly />)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </section>
      )}

      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
        onChange={uploadPhoto} data-testid="wish-photo-input" />
      {itemDialog && (
        <WishItemDialog key={itemDialog.item?.id || "new"} open onOpenChange={() => setItemDialog(null)}
          item={itemDialog.item} listId={itemDialog.list.id} onSaved={refresh} />
      )}
      {shareList && (
        <ShareDialog open onOpenChange={() => setShareList(null)} list={shareList} onSaved={refresh} />
      )}
      <Dialog open={newListOpen} onOpenChange={setNewListOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="wishlist-new-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nueva lista de deseos</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="wishlist-new-list-input" value={newListName} onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createList()} placeholder="Ej: Lista Regalos" maxLength={100} autoFocus />
            <Button className="w-full" onClick={createList} data-testid="wishlist-new-list-save">Crear lista</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
